/**
 * SCR-013 — Student profile detail: 4 tabs — Thông tin, Phụ huynh, Ghi danh, Điểm danh.
 * Admin: all students. Manager: own branch only (API enforces).
 */
import { createFileRoute, Link } from '@tanstack/react-router';
import { format } from 'date-fns';
import { AlertCircle, Pencil } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { StudentForm, type StudentFormValues } from '@client/components/forms/student-form';
import { isApiError, useBranches } from '@client/lib/queries/use-branches';
import {
  useStudent,
  useUpdateStudent,
  useStudentEnrollments,
  useStudentParents,
  type StudentStatus,
} from '@client/lib/queries/use-students';

export const Route = createFileRoute('/admin/students/$studentId')({
  component: StudentDetailPage,
});

const STATUS_META: Record<StudentStatus, { label: string; variant: 'success' | 'secondary' | 'info' | 'destructive' }> = {
  active:    { label: 'Đang học',    variant: 'success' },
  inactive:  { label: 'Tạm nghỉ',    variant: 'secondary' },
  graduated: { label: 'Tốt nghiệp',  variant: 'info' },
  withdrawn: { label: 'Đã nghỉ',     variant: 'destructive' },
};

const GENDER_LABELS: Record<string, string> = {
  male: 'Nam', female: 'Nữ', other: 'Khác',
};

const ENROLLMENT_STATUS_META: Record<string, { label: string; variant: 'success' | 'warning' | 'info' | 'destructive' }> = {
  active:      { label: 'Đang học',     variant: 'success' },
  transferred: { label: 'Chuyển lớp',   variant: 'warning' },
  completed:   { label: 'Hoàn thành',   variant: 'info' },
  dropped:     { label: 'Đã nghỉ',      variant: 'destructive' },
};

function InfoRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{label}</span>
      <span className={`text-sm text-foreground ${mono ? 'font-mono' : ''}`}>{value || '—'}</span>
    </div>
  );
}

function StudentDetailPage() {
  const { studentId } = Route.useParams();

  const { data: student, isLoading, isError } = useStudent(studentId);
  const updateStudent = useUpdateStudent(studentId);
  const { data: enrollmentsData } = useStudentEnrollments(studentId);
  const { data: parentsData } = useStudentParents(studentId);
  const { data: branchesData } = useBranches({ status: 'active', pageSize: 100 });

  const enrollments = enrollmentsData?.items ?? [];
  const parents = parentsData ?? [];
  const branches = branchesData?.items ?? [];

  const [editOpen, setEditOpen] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  async function handleSubmit(values: StudentFormValues) {
    setFormError(null);
    try {
      const payload = {
        ...values,
        gender: values.gender || undefined,
        phone: values.phone || undefined,
        email: values.email || undefined,
        address: values.address || undefined,
        note: values.note || undefined,
        enrolledAt: values.enrolledAt || undefined,
      };
      await updateStudent.mutateAsync(payload);
      toast.success('Đã cập nhật hồ sơ học viên.');
      setEditOpen(false);
    } catch (err) {
      setFormError(isApiError(err) ? err.message : 'Lỗi máy chủ, thử lại sau.');
    }
  }

  if (isLoading) return <StudentDetailSkeleton />;

  if (isError || !student) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Không tải được hồ sơ học viên.{' '}
          <Link to="/admin/students" className="underline">Quay lại</Link>
        </AlertDescription>
      </Alert>
    );
  }

  const statusMeta = STATUS_META[student.status] ?? STATUS_META['active'];

  return (
    <div className="flex flex-col gap-6">
      {/* Breadcrumb */}
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/admin/students">Học viên</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>{student.fullName}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3 flex-wrap">
          {/* Avatar */}
          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm shrink-0">
            {student.fullName.charAt(0).toUpperCase()}
          </div>
          <div className="flex flex-col">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-bold text-foreground">{student.fullName}</h1>
              <Badge variant={statusMeta.variant} className="text-xs">
                {statusMeta.label}
              </Badge>
            </div>
            <span className="text-xs text-muted-foreground font-mono">
              {student.studentCode}
              {student.branchName ? ` · ${student.branchName}` : ''}
            </span>
          </div>
        </div>
        <Button
          className="self-start sm:self-auto"
          onClick={() => { setFormError(null); setEditOpen(true); }}
        >
          <Pencil className="h-4 w-4 mr-1.5" /> Sửa
        </Button>
      </div>

      {/* Tabs — vertical rail (mobile-first: stacks above content <768px) */}
      <Tabs
        defaultValue="info"
        orientation="vertical"
        className="flex-col gap-4 md:flex-row md:gap-6"
      >
        <TabsList
          variant="line"
          className="w-full shrink-0 md:w-48 md:border-r md:pr-2"
        >
          <TabsTrigger value="info">Thông tin</TabsTrigger>
          <TabsTrigger value="parents">
            Phụ huynh {parents.length > 0 ? `(${parents.length})` : ''}
          </TabsTrigger>
          <TabsTrigger value="enrollments">
            Ghi danh {enrollments.length > 0 ? `(${enrollments.length})` : ''}
          </TabsTrigger>
          <TabsTrigger value="attendance">Điểm danh</TabsTrigger>
        </TabsList>

        {/* ── Tab 1: Info ── */}
        <TabsContent value="info" className="mt-0">
          <Card>
            <CardContent className="pt-4">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <InfoRow label="Mã học viên" value={student.studentCode} mono />
                <InfoRow label="Cơ sở" value={student.branchName ?? ''} />
                <InfoRow
                  label="Ngày sinh"
                  value={student.dateOfBirth
                    ? format(new Date(student.dateOfBirth), 'dd/MM/yyyy')
                    : ''}
                />
                <InfoRow
                  label="Giới tính"
                  value={student.gender ? (GENDER_LABELS[student.gender] ?? student.gender) : ''}
                />
                <InfoRow label="SĐT" value={student.phone ?? ''} />
                <InfoRow label="Email" value={student.email ?? ''} />
                {student.address && (
                  <div className="md:col-span-2 flex flex-col gap-0.5">
                    <span className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Địa chỉ</span>
                    <p className="text-sm">{student.address}</p>
                  </div>
                )}
                <InfoRow
                  label="Ngày vào trung tâm"
                  value={student.enrolledAt
                    ? format(new Date(student.enrolledAt), 'dd/MM/yyyy')
                    : ''}
                />
                {student.note && (
                  <div className="md:col-span-2 flex flex-col gap-0.5">
                    <span className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Ghi chú</span>
                    <p className="text-sm">{student.note}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Tab 2: Parents ── */}
        <TabsContent value="parents" className="mt-0">
          {parents.length === 0 ? (
            <p className="text-muted-foreground text-sm text-center py-8">
              Chưa liên kết phụ huynh.
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {parents.map((p) => (
                <div
                  key={p.id}
                  className="flex items-center justify-between rounded-lg border p-3 gap-3"
                >
                  <div className="flex flex-col gap-0.5">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{p.parentName ?? p.parentId}</span>
                      {p.isPrimary && (
                        <Badge variant="success" className="text-xs">
                          Liên hệ chính
                        </Badge>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {p.relation}
                      {p.parentPhone ? ` · ${p.parentPhone}` : ''}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ── Tab 3: Enrollments ── */}
        <TabsContent value="enrollments" className="mt-0">
          {enrollments.length === 0 ? (
            <p className="text-muted-foreground text-sm text-center py-8">
              Chưa ghi danh lớp nào.
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {enrollments.map((e) => {
                const meta = ENROLLMENT_STATUS_META[e.status] ?? ENROLLMENT_STATUS_META['active'];
                return (
                  <div key={e.id} className="flex items-center justify-between rounded-lg border p-3 gap-3">
                    <div className="flex flex-col gap-0.5">
                      <Link
                        to="/admin/classes/$classId"
                        params={{ classId: e.classId }}
                        className="font-medium text-sm hover:underline text-primary"
                      >
                        {e.className ?? e.classId}
                      </Link>
                      <span className="text-xs text-muted-foreground">
                        {e.classCode ? `${e.classCode} · ` : ''}
                        {e.branchName ?? ''}
                        {e.enrolledAt
                          ? ` · Ghi danh ${format(new Date(e.enrolledAt), 'dd/MM/yyyy')}`
                          : ''}
                      </span>
                    </div>
                    <Badge variant={meta?.variant} className="text-xs">
                      {meta?.label}
                    </Badge>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ── Tab 4: Attendance (read-only summary) ── */}
        <TabsContent value="attendance" className="mt-0">
          <p className="text-muted-foreground text-sm text-center py-8">
            Dữ liệu điểm danh sẽ hiển thị khi học viên đã tham gia buổi học.
          </p>
        </TabsContent>
      </Tabs>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-lg w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Sửa hồ sơ học viên</DialogTitle>
          </DialogHeader>
          {formError && (
            <Alert variant="destructive" className="mt-2">
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          )}
          <StudentForm
            isEdit
            isSubmitting={updateStudent.isPending}
            branches={branches}
            defaultValues={{
              studentCode: student.studentCode,
              branchId: student.branchId,
              fullName: student.fullName,
              dateOfBirth: student.dateOfBirth,
              gender: student.gender ?? '',
              phone: student.phone ?? '',
              email: student.email ?? '',
              address: student.address ?? '',
              enrolledAt: student.enrolledAt ?? '',
              status: student.status,
              note: student.note ?? '',
            }}
            onSubmit={handleSubmit}
            onCancel={() => setEditOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StudentDetailSkeleton() {
  return (
    <div className="flex flex-col gap-6">
      <Skeleton className="h-5 w-40" />
      <div className="flex items-center gap-3">
        <Skeleton className="h-10 w-10 rounded-full" />
        <div className="flex flex-col gap-1">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-32" />
        </div>
      </div>
      <Skeleton className="h-10 w-full" />
      <Card>
        <CardContent className="grid grid-cols-2 gap-3 pt-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
